import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';

import { IHome } from '@/modules/home/types';
import axiosClient from '@/shared/lib/axios';

/************************************ HOME PAGE OPERATIONS WIDGETS *********************************************/
export const getOperationsWidgets = async ({
  entityId,
  date,
}: // cropId
IHome.IWidgetsProps): Promise<IHome.IOperationsWidgetsSchema> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/home-page/operations`,
    {
      params: {
        entityId,
        date,
      },
    }
  );
  return data;
};

export const useGetOperationsWidgets = ({
  entityId,
  date,
}: IHome.IWidgetsProps) => {
  return useQuery({
    queryKey: ['getOperationsWidgets', entityId, date],
    queryFn: () => getOperationsWidgets({ entityId, date }),
    enabled: !!entityId && !!date,
  });
};
/************************************ HOME PAGE WORKFORCE WIDGETS *********************************************/
export const getWorkforceWidgets = async ({
  entityId,
  date,
}: // cropId
IHome.IWidgetsProps): Promise<IHome.IWorkforceWidgetSchema> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/home-page/workforce/hero-numbers`,
    {
      params: {
        entityId,
        date,
        // cropId
      },
    }
  );
  return data;
};

export const useGetWorkforceWidgets = ({
  entityId,
  date,
}: // cropId
IHome.IWidgetsProps) => {
  return useQuery({
    queryKey: ['getWorkforceWidgets', entityId, date],
    queryFn: () => getWorkforceWidgets({ entityId, date }),
    enabled: !!entityId && !!date,
  });
};
/************************************ WORKFORCE DISTRIBUTION FIELDS *********************************************/
export const getWorkforceDistributionFields = async ({
  entityId,
  date,
}: // cropId
IHome.IWidgetsProps): Promise<IHome.IWorkforceDistributionItemSchema[]> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/home-page/workforce-fields`,
    {
      params: {
        entityId,
        date,
        // cropId
      },
    }
  );
  return data;
};

export const useGetWorkforceDistributionFields = () => {
  return useMutation(
    ['getWorkforceDistributionFields'],
    getWorkforceDistributionFields
  );
};
/************************************ WORKFORCE DISTRIBUTION FIELDS *********************************************/
export const getWorkforceDistributionOperations = async ({
  entityId,
  date,
}: // cropId
IHome.IWidgetsProps): Promise<IHome.IWorkforceDistributionItemSchema[]> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/home-page/workforce-operations`,
    {
      params: {
        entityId,
        date,
        // cropId
      },
    }
  );
  return data;
};

export const useGetWorkforceDistributionOperations = () => {
  return useMutation(
    ['getWorkforceDistributionOperations'],
    getWorkforceDistributionOperations
  );
};
/************************************ WORKFORCE RESOURCES SUPERVISOR *********************************************/
export const getWorkforceResourcesSupervisors = async ({
  entityId,
  date,
}: // cropId
IHome.IWidgetsProps): Promise<IHome.IWorkforceResourcesSupervisorSchema[]> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/home-page/resources/supervisors`,
    {
      params: {
        entityId,
        date,
        // cropId
      },
    }
  );
  return data;
};

export const useGetWorkforceResourcesSupervisors = ({
  entityId,
  date,
}: // cropId
IHome.IWidgetsProps) => {
  return useQuery({
    queryKey: ['getWorkforceResourcesSupervisors', entityId, date],
    queryFn: () => getWorkforceResourcesSupervisors({ entityId, date }),
    enabled: !!entityId && !!date,
  });
};
/************************************ WORKFORCE RESOURCES CREWS *********************************************/
export const getWorkforceResourcesCrews = async ({
  entityId,
  date,
}: // cropId
IHome.IWidgetsProps): Promise<IHome.IWorkforceResourcesCrewSchema[]> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/home-page/resources/crews`,
    {
      params: {
        entityId,
        date,
        // cropId
      },
    }
  );
  return data;
};

export const useGetWorkforceResourcesCrews = () => {
  return useMutation(
    ['getWorkforceResourcesCrews'],
    getWorkforceResourcesCrews
  );
};

/************************************ HOME PAGE FIELDS SECTIONS *********************************************/

export const getHomeFields = async ({
  entityId,
  date,
  cropId,
}: IHome.IGetFieldsProps): Promise<IHome.IField[]> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/home-page/fields`,
    {
      params: {
        entityId,
        date,
        cropId,
      },
    }
  );
  return data;
};

export const useGetHomeFields = ({
  entityId,
  date,
  cropId,
}: IHome.IGetFieldsProps) => {
  return useQuery({
    queryKey: ['getHomeFields', entityId, date, cropId],
    queryFn: () => getHomeFields({ entityId, date, cropId }),
    enabled: !!entityId && !!date,
  });
};
/************************************ HOME PAGE TIMELINE *********************************************/
// GET TIMELINE OPERATIONS
const getTimelineOperations = async (params: IHome.IGetTimelineSchema) => {
  const response = await axiosClient.get<IHome.IGetTimelineOperationsResponse>(
    `/company/portalInsights/home-page/timeline/operations`,
    { params }
  );

  return {
    items: response?.data?.output,
    nextPage:
      params?.pageNumber * params?.pageSize < response?.data?.totalCount
        ? params?.pageNumber + 1
        : undefined,
  };
};
export const useGetTimelineOperations = (params: IHome.IGetTimelineSchema) => {
  return useInfiniteQuery({
    queryKey: ['getTimelineOperations', params],
    queryFn: ({ pageParam }) =>
      getTimelineOperations({
        ...params,
        pageNumber: pageParam ?? params.pageNumber,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,

    enabled: !!params.entityId && !!params.date,
  });
};

// GET TIMELINE ALERTS
const getTimelineAlerts = async (params: IHome.IGetTimelineSchema) => {
  const response = await axiosClient.get<IHome.IGetTimelineAlertsResponse>(
    `/company/portalInsights/home-page/timeline/alerts`,
    { params }
  );
  return {
    items: response?.data.workerAlerts,
    nextPage:
      params?.pageNumber * params?.pageSize < response?.data?.totalCount
        ? params?.pageNumber + 1
        : undefined,
  };
};
export const useGetTimelineAlerts = (params: IHome.IGetTimelineSchema) => {
  return useInfiniteQuery({
    queryKey: ['getTimelineAlerts', params],
    queryFn: ({ pageParam }) =>
      getTimelineAlerts({
        ...params,
        pageNumber: pageParam ?? params.pageNumber,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,

    enabled: !!params.entityId && !!params.date,
  });
};

/************************************ INPUTS *********************************************/

// Pending requests
const getInventoryPendingRequests = async ({
  entityId,
  date,
  page,
  limit,
}: IHome.IGetInputsProps): Promise<
  IHome.IGetInputsResponse<IHome.IInventoryPendingRequest>
> => {
  const response = await axiosClient.get(
    `inventory/api/v1/dispatch-request/pending/entity/${entityId}`,
    {
      params: {
        date,
        page,
        limit,
      },
    }
  );
  return {
    ...response.data,
    nextPage: page * limit < response?.data?.totalItems ? page + 1 : undefined,
  };
};

export const useGetInventoryPendingRequests = ({
  entityId,
  date,
  page,
  limit,
  isInventoryActive,
}: IHome.IGetInputsProps) => {
  return useInfiniteQuery({
    queryKey: [
      'getInventoryPendingRequests',
      entityId,
      date,
      page,
      limit,
      isInventoryActive,
    ],
    queryFn: ({ pageParam }) =>
      getInventoryPendingRequests({
        entityId,
        date,
        page: pageParam ?? page,
        limit,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,

    enabled: !!entityId && !!date && !!isInventoryActive,
  });
};

//Transactions
const getInventoryTransactions = async ({
  entityId,
  date,
  page,
  limit,
}: IHome.IGetInputsProps): Promise<
  IHome.IGetInputsResponse<IHome.IInventoryTransactions>
> => {
  const response = await axiosClient.get(
    `/inventory/api/v1/warehouses-batches/entity/${entityId}`,
    {
      params: {
        date,
        page,
        limit,
      },
    }
  );
  return {
    ...response.data,
    nextPage: page * limit < response?.data?.totalItems ? page + 1 : undefined,
  };
};

export const useGetInventoryTransactions = ({
  entityId,
  date,
  page,
  limit,
  isInventoryActive,
}: IHome.IGetInputsProps) => {
  return useInfiniteQuery({
    queryKey: [
      'getInventoryTransactions',
      entityId,
      date,
      page,
      limit,
      isInventoryActive,
    ],
    queryFn: ({ pageParam }) =>
      getInventoryTransactions({
        entityId,
        date,
        page: pageParam ?? page,
        limit,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!entityId && !!date && !!isInventoryActive,
  });
};

//  Latest Periodic Operations
const GetLatestPeriodicOperations = async ({
  entityId,
  date,
  page,
  limit,
}: IHome.IGetInputsProps): Promise<
  IHome.IGetInputsResponse<IHome.IPeriodicOperation>
> => {
  const response = await axiosClient.get(
    `/inventory/api/v1/periodic-operation/latest/entity/${entityId}`,
    {
      params: {
        date,
        page,
        limit,
      },
    }
  );
  return {
    ...response.data,
    nextPage: page * limit < response?.data?.totalItems ? page + 1 : undefined,
  };
};

export const useGetLatestPeriodicOperations = ({
  entityId,
  date,
  page,
  limit,
}: IHome.IGetInputsProps) => {
  return useInfiniteQuery({
    queryKey: ['getLatestPeriodicOperations', entityId, date, page, limit],
    queryFn: ({ pageParam }) =>
      GetLatestPeriodicOperations({
        entityId,
        date,
        page: pageParam ?? page,
        limit,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!entityId && !!date,
  });
};

//*********************************** Approvals ****************************************************************/

const getHomeApprovals = async (
  params: IHome.IGetApprovalsProps
): Promise<IHome.IGetApprovalsResponse> => {
  const response = await axiosClient.get(
    `/company/portalInsights/home-page/approvals`,
    { params }
  );
  return {
    ...response.data,
    nextPage:
      params?.pageNumber * params?.pageSize < response?.data?.totalCount
        ? params?.pageNumber + 1
        : undefined,
  };
};

export const useGetHomeApprovals = (params: IHome.IGetApprovalsProps) => {
  return useInfiniteQuery({
    queryKey: ['getHomeApprovals', params],
    queryFn: ({ pageParam }) =>
      getHomeApprovals({
        ...params,
        pageNumber: pageParam ?? params.pageNumber,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!params.entityId && !!params.pageNumber && !!params.pageSize,
  });
};

/************************************ END HOME PAGE TIMELINE *********************************************/
